<template>
  <div id="dashboard" class="page">
    <v-main>
      <AppTopPanel source-type="dashboard" :show-back-button="false" />
      <v-divider color="#f7f7f7" />
      <v-container fluid class="slick-cactus-container slick-cactus-container--dashboard">
        <v-skeleton-loader
          :loading="isLayoutSettingsLoading"
          type="image@5"
          class="dashboard__skeleton-wrapper"
        >
          <v-row>
            <v-col
              :cols="getSectionData(userWidgetsEnum.tasks).cols"
              :order="getSectionData(userWidgetsEnum.tasks).order"
            >
              <Tasks
                :expanded="getSectionData(userWidgetsEnum.tasks).is_toggled"
                @update:expanded="updateWidgets"
              />
            </v-col>
            <v-col
              :cols="getSectionData(userWidgetsEnum.critical_dates).cols"
              :order="getSectionData(userWidgetsEnum.critical_dates).order"
            >
              <CriticalDatesDashboardWidget
                :expanded="getSectionData(userWidgetsEnum.critical_dates).is_toggled"
                @update:expanded="updateWidgets"
              />
            </v-col>
            <v-col
              :cols="getSectionData(userWidgetsEnum.open_balances).cols"
              :order="getSectionData(userWidgetsEnum.open_balances).order"
            >
              <BrokerProductionCard
                report-type="unpaid"
                :expanded="getSectionData(userWidgetsEnum.open_balances).is_toggled"
                @update:expanded="updateWidgets"
              />
            </v-col>
            <v-col
              v-if="displayAgentRanking"
              :cols="getSectionData(userWidgetsEnum.broker_ranking).cols"
              :order="getSectionData(userWidgetsEnum.broker_ranking).order"
            >
              <BrokerRankingCard
                :card-expanded="getSectionData(userWidgetsEnum.broker_ranking).is_toggled"
                @update:expanded="updateWidgets"
              />
            </v-col>
            <v-col
              :cols="getSectionData(userWidgetsEnum.new_availabilities).cols"
              :order="getSectionData(userWidgetsEnum.new_availabilities).order"
            >
              <DashboardAvailabilitiesCard
                :expanded="getSectionData(userWidgetsEnum.new_availabilities).is_toggled"
                :display-brokers="true"
                :display-owners="true"
                @update:expanded="updateWidgets"
              />
            </v-col>
            <v-col
              :cols="getSectionData(userWidgetsEnum.recent_comparables).cols"
              :order="getSectionData(userWidgetsEnum.recent_comparables).order"
            >
              <DashboardComparablesCard
                :expanded="getSectionData(userWidgetsEnum.recent_comparables).is_toggled"
                @update:expanded="updateWidgets"
              />
            </v-col>
            <v-col
              :cols="getSectionData(userWidgetsEnum.open_invoices).cols"
              :order="getSectionData(userWidgetsEnum.open_invoices).order"
            >
              <DashboardOpenInvoicesCard
                :expanded="getSectionData(userWidgetsEnum.open_invoices).is_toggled"
                @update:expanded="updateWidgets"
              />
            </v-col>
          </v-row>
        </v-skeleton-loader>
      </v-container>
    </v-main>
    <Footer />
  </div>
</template>

<script lang="ts">
import { mapState } from 'vuex'
import mixins from 'vue-typed-mixins';

import { DashboardLayoutMixin } from '@/bundles/Settings/mixins/DashboardLayoutMixin';
import { UserWidgetsEnum } from '@/bundles/Settings/enums/userLayoutEnum';
import { IUserLayoutWidget } from '@/bundles/Settings/interfaces/IUserLayoutWidget';

import BrokerRankingCard from '@/components/reports/BrokerRankingCard.vue';
import BrokerProductionCard from '@/components/reports/BrokerProductionCard.vue';
import AppTopPanel from '@/components/common-components/AppTopPanel.vue';
import Footer from '@/components/Footer.vue';
import Tasks from '@/bundles/Tasks/components/Tasks.vue';
import CriticalDatesDashboardWidget from '@/bundles/CriticalDates/components/CriticalDatesDashboardWidget.vue';
import DashboardAvailabilitiesCard from '@/bundles/Availabilities/components/cards/DashboardAvailabilitiesCard.vue';
import DashboardComparablesCard from '@/bundles/Comparables/components/cards/DashboardComparablesCard.vue';
import DashboardOpenInvoicesCard from '@/bundles/Dashboard/components/DashboardOpenInvoicesCard.vue';

export default mixins(DashboardLayoutMixin).extend({
  name: 'Dashboard',

  components: {
    DashboardComparablesCard,
    DashboardAvailabilitiesCard,
    AppTopPanel,
    Footer,
    Tasks,
    BrokerRankingCard,
    BrokerProductionCard,
    CriticalDatesDashboardWidget,
    DashboardOpenInvoicesCard
  },

  computed: {
    ...mapState({
      navState: (state: any) => state.nav,
      activeAccountId: (state: any) => state.activeAccount.id,
      displayDashboardAgentRanking: (state: any) => !!state.accountSettings.accounting?.display_dashboard_agent_ranking,
    }),
    userWidgetsEnum (): typeof UserWidgetsEnum {
      return UserWidgetsEnum;
    },
    displayAgentRanking (): boolean {
      if (this.isSuperAdmin || this.isFinancialAdmin) return true;

      return this.displayDashboardAgentRanking;
    }
  },

  methods: {
    getSectionData (sectionName: UserWidgetsEnum): IUserLayoutWidget {
      return this.dashboardWidgets.find((section) => section.name === sectionName) || { cols: 12 } as IUserLayoutWidget;
    },
    updateWidgets ({ name, value }: { name: UserWidgetsEnum, value: boolean }) {
      const payload = this.dashboardWidgets.map((section) => {
        if (section.name === name) {
          section.is_toggled = value;
        }

        return section;
      });

      this.saveOrder(payload);
    },
  }
});
</script>

<style lang="scss">
.dashboard__skeleton-wrapper {
  .v-skeleton-loader__image {
    margin-bottom: 12px;
    height: 80px;
  }
}
</style>
